import { IntegrationLogResponse } from '@monetize/types/app';
import { FiExternalLink } from 'react-icons/fi';
import {
  IntegrationLogStatusTagStyle,
  IntegrationStatusEnumDisplay,
} from '../IntegrationLog';
import { MBox, MFlex, MHStack, MIcon, MLink, MText } from '../Monetize';
import { DateDisplay } from '../Monetize/DateDisplay';
import { EXTERNAL_SYSTEM_ICONS } from './externalSystemIcons';
import { IntegrationSystemSummary } from './useIntegrationInfo';

interface IntegrationSystemRowProps {
  summary: IntegrationSystemSummary;
  onSelectLog: (log: IntegrationLogResponse) => void;
}

/**
 * One external system on a record: what it is linked to and how its last sync
 * went, in a single row. The connector stores one sync log per record per
 * system and updates it in place, so a per-system row — rather than a feed of
 * log entries — is the shape of the data.
 */
export const IntegrationSystemRow = ({
  summary,
  onSelectLog,
}: IntegrationSystemRowProps) => {
  const { system, label, references, latestLog, hasFailure, isNotConnected } =
    summary;

  return (
    <MBox
      position="relative"
      px={2}
      py={2}
      borderRadius="md"
      _hover={latestLog ? { bg: 'bg.hover' } : undefined}
    >
      {latestLog && (
        // Stretched hit area: the whole row opens the log, while the external
        // link stays clickable above it. An <a> nested inside a <button> would
        // be invalid markup and unreachable.
        <MBox
          asChild
          onClick={() => onSelectLog(latestLog)}
          aria-label={`Sync log ${label} ${latestLog.eventType} ${latestLog.status}`}
          position="absolute"
          inset={0}
          borderRadius="md"
          _focusVisible={{ boxShadow: 'focus' }}
        >
          <button type="button" />
        </MBox>
      )}

      {/* Text ignores pointer events so clicks fall through to the hit area;
          the link opts back in. */}
      <MBox position="relative" pointerEvents="none">
        <MFlex justify="space-between" align="center" gap={2}>
          <MHStack gap={1} minW={0}>
            <MIcon as={EXTERNAL_SYSTEM_ICONS[system]} boxSize="14px" />
            <MText fontWeight="600" lineClamp={1}>
              {label}
            </MText>
          </MHStack>

          {latestLog ? (
            <MHStack gap={2} flexShrink={0}>
              <MText
                h="18px"
                lineHeight="18px"
                {...IntegrationLogStatusTagStyle[latestLog.status]}
              >
                {IntegrationStatusEnumDisplay[latestLog.status]}
              </MText>
              <DateDisplay
                date={latestLog.lastUpdatedAt}
                timezoneConfigKey="userTimezone"
                textProps={{ color: 'fg.muted', whiteSpace: 'nowrap' }}
              />
            </MHStack>
          ) : (
            // A not-connected row says so below; saying it twice adds nothing.
            !isNotConnected && (
              <MText color="fg.muted" flexShrink={0}>
                No sync activity
              </MText>
            )
          )}
        </MFlex>

        {references.map(({ id, url }) => (
          // Ids opt back into pointer events: linked ones need the click, and
          // detected ones are worth being able to select and copy.
          <MText
            key={id}
            pl={5}
            lineClamp={1}
            pointerEvents="auto"
            w="fit-content"
          >
            {url ? (
              <MLink
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                color="tIndigo.base"
              >
                {id}
                <MIcon as={FiExternalLink} ml="1" color="tIndigo.base" />
              </MLink>
            ) : (
              id
            )}
          </MText>
        ))}

        {isNotConnected && (
          <MText pl={5} color="fg.muted">
            Not connected
          </MText>
        )}

        {hasFailure && latestLog?.log && (
          <MText pl={5} color="fg.muted" lineClamp={2}>
            {latestLog.log}
          </MText>
        )}
      </MBox>
    </MBox>
  );
};
